// SELF — the PANTHEON (Joel's mockup, 2026-10-10): a marble rotunda with one
// room per Step. Each room holds only that Step's three deities (DEMI · TYTN ·
// OLMPN) — the working tier front and centre, the other two beside — and no
// silhouettes. The rooms, the oculus and the centre arch are open, so the
// app's own sky / night shows through them.
//
// Changing view (toggle Maladies · Current · Deities, or a horizontal swipe)
// TURNS THE WHOLE ROTUNDA about its axis, 120° a view — three views are one
// full turn. The wall ring swings round a cylinder (drawn as thin strips at
// x = R·sin(φ−θ)), carrying its rooms and their figures; the dome spins round
// the oculus overhead and the floor medallion spins in place underfoot, both
// in true perspective. Tap a figure to talk to it. ▼ returns to the mountain.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Easing, Image, PanResponder, Platform, Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { BlurView } from 'expo-blur';
import Svg, { Circle, Defs, Ellipse, Path, Polygon, RadialGradient, Stop } from 'react-native-svg';
import { TIER_LABEL, TIER_ORDER, type GearId, type TierId } from '../olmps/ascension';
import type { SelfState, SeatView } from './useSelf';
import type { NowStep } from './useCurrentStep';
import { GEAR_LABEL, SEAT_BY_ID, fullBodyFor } from './roster';
import { HALL_CLIPS } from './hallClips';
import { HallFigure } from './HallFigure';
import { maladyFullFor } from './maladyCutouts';
import { SHAPE_BY_ID, shapeFullFor } from './shapeRoster';
import { useShapeSeats, type ShapeSeats } from './useShapeSeats';
import { SECTOR_LABELS, type Sector } from '../domain/olmps';
import { EdgeTabs } from './EdgeTabs';
import { tourBus, useOnboarding, useTourTarget } from './onboarding';
import { DeityFocus } from './DeityFocus';
import { openTalk } from './TalkingOverlay';
import { getCouncilRequest, onCouncil, closeCouncil } from '../live/appActions';
import { useCouncil, SLOTS, seatName as councilSeatName } from '../council/useCouncil';

import { CouncilButtons, CouncilComposer, CouncilHeader, CouncilPicker, CouncilSeatMenu, CouncilSessions, CouncilTranscript, type CouncilPanel } from '../council/CouncilPanels';
import { Lock } from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ScreenHelp } from './ScreenHelp';
import { pantheonHelp } from '../olmps/help/pantheonHelp';
import { DISPLAY_FONT, makeStyles, useV10 } from './theme';
import {
  CYL_CX, DOME, FLOOR_TOP, FOOT_Y, MEDALLION, PANTHEON_DOME, PANTHEON_FLOOR, PANTHEON_MEDALLION, PANTHEON_WALLS,
  HORIZON_Y, PANTHEON_PODIUM, VIEW_D, PLATE_CROP, PLATE_H, PLATE_W, PODIUM, R_CYL, ROOM_FOOT, ROOMS, TITLE_Y, TOGGLE_W, TOGGLE_Y, TURN_DEG, WALL_BOTTOM, WALL_TEX_W, wallU, type Room,
} from './pantheonLayout';

/** RN 0.81 honours pointerEvents in style, but ImageStyle typings omit it. */
const NO_TOUCH = { pointerEvents: 'none' } as object;

// The hall has three FLOORS (the vertical stack at the left edge: ORDER · SHAPE · PAN), and on each
// floor the dial turns the rotunda through three stops:
//   ORDER — the three tiers, one ORDER gallery each: a tier's five deities, one per room (all 15 seats).
//   SHAPE — GROUP 2 (the middle, where you land) shows each sector's podium with the deity ATLAS picked
//           for it (rooms laid out like the Mountain's sectors); GROUP 1 and 3 are locked for now.
//   PAN   — COUNCIL first: each room's podium is a council seat (the deity you put there, or an empty
//           podium with +), SyQi's box on the medallion; the other two stops are locked for now.
// Changing floor slides the whole hall off the screen (up when going down a floor) and the next floor
// in from the other side, the way the screens above and below do.
type Mode = TierId;
type Level = 'order' | 'shape' | 'pan';
const LEVELS: { id: Level; label: string }[] = [{ id: 'order', label: 'ORDER' }, { id: 'shape', label: 'SHAPE' }, { id: 'pan', label: 'PAN' }];
// The ORDER dial names the views as ORDER galleries (five seats each); the tier ids stay underneath.
const MODES: { id: Mode; label: string }[] = TIER_ORDER.map((tr, i) => ({ id: tr, label: `ORDER ${i + 1}` }));
/** Each floor's three dial stops; a locked stop is drawn dark with a padlock and the ring won't turn to it. */
const STOPS: Record<Level, { label: string; locked?: boolean }[]> = {
  order: MODES.map((m) => ({ label: m.label })),
  shape: [{ label: 'GROUP 1', locked: true }, { label: 'GROUP 2' }, { label: 'GROUP 3', locked: true }],
  pan: [{ label: 'COUNCIL' }, { label: 'PAN 2', locked: true }, { label: 'PAN 3', locked: true }],
};
const LAST_IDX = 2;
/** Who stands on an ORDER podium (the stack right of the dial): whoever holds the seat now, always the deity, or always the malady. */
type Who = 'current' | 'deity' | 'malady';
const WHOS: { id: Who; label: string }[] = [{ id: 'current', label: 'CURRENT' }, { id: 'deity', label: 'DEITY' }, { id: 'malady', label: 'MALADY' }];
/** The dial bends with the roof's coffer band: a shallow smile, sagging this share of its width at the middle. */
const DIAL_SAG = 0.16;
const DIAL_H = 32;
/** The floor stack: one pill per floor, down the left edge under the dial's end. */
const LEVEL_W = 62;
const WHO_W = 78;
const LEVEL_H = 28;
const LEVEL_MS = 900;
const ROOM_SECTOR: Record<GearId, Sector> = { orient: 'S', revise: 'P', regulate: 'A', execute: 'E', direct: 'H' };
/** What a room shows in COUNCIL mode: five podiums (ROOMS order), anyone on any of them. */
interface CouncilSeats {
  bySlot: string[];
  speaking: string | null;
  thinking: string | null;
  muted: string[];
  onEmpty: (slot: number) => void;
  onSeat: (id: string) => void;
}
const TURN_MS = 900;
/** Height kept for the chat box at the bottom while a deity is in focus. */
const FOCUS_CHAT_H = 66;
/** Her opening bubble above the chat box (kept clear of her podium). */
const FOCUS_OPENER_H = 76;
// Wall strips per view (2.5° each on web — finer = no stair-steps on the cornice mid-turn). iOS pays
// per animated view per frame (Fabric updates each one natively), so the phone uses 5° strips: half
// the views, and the facet tilt still joins them edge to edge.
const STRIPS = Platform.OS === 'web' ? 48 : 24;
const STRIP_W = WALL_TEX_W / STRIPS;
/** Wall strip views span HORIZON_Y ± STRIP_HALF (plate px) so they pivot on the horizon. */
const STRIP_HALF = 1000;
const RAD = Math.PI / 180;

/** pos (0..2, the dial stop) → θ: sampled every 5° so Animated can carry sin/cos on the native thread. */
const POS_RANGE: number[] = [];
for (let th = -TURN_DEG; th <= (LAST_IDX + 2) * TURN_DEG; th += 5) POS_RANGE.push(th / TURN_DEG);
/** An angle folded into −180..180°. */
const wrap180 = (deg: number) => ((((deg + 180) % 360) + 360) % 360) - 180;
/** Where a floor's dial sits when you arrive on it: ORDER on your working tier, SHAPE on GROUP 2, PAN on COUNCIL. */
const homeStop = (lv: Level, tier: TierId) => (lv === 'order' ? TIER_ORDER.indexOf(tier) : lv === 'shape' ? 1 : 0);
/** The unlocked stops a swipe may reach from `at` without crossing a locked one: [lo, hi]. */
function reach(lv: Level, at: number): [number, number] {
  const st = STOPS[lv];
  let lo = at;
  let hi = at;
  while (lo > 0 && !st[lo - 1].locked) lo--;
  while (hi < LAST_IDX && !st[hi + 1].locked) hi++;
  return [lo, hi];
}
const sampled = (pos: Animated.Value, f: (thDeg: number) => number) =>
  pos.interpolate({ inputRange: POS_RANGE, outputRange: POS_RANGE.map((p) => f(p * TURN_DEG)), extrapolate: 'clamp' });

/** Where something at wall angle φ (deg) is when the rotunda has turned θ. */
/** Facet perspective, in plate px (small → small tilts; only the ratio matters). */
const FACET_P = R_CYL * 0.25;
const persp = (deg: number) => (VIEW_D + 1) / (VIEW_D + Math.cos(Math.max(-89, Math.min(89, wrap180(deg))) * RAD));

/** Where something at wall angle φ (deg) is when the rotunda has turned θ.
 *  restDeg = the angle it was drawn at in the plate; midY = its view's centre
 *  (plate px). Horizontally it rides the cylinder; vertically it rescales about
 *  the horizon by g(now)/g(rest), so wall tops settle onto the cornice line. */
function onCylinder(pos: Animated.Value, phiDeg: number, rS: number, restDeg: number, midY: number, s: number) {
  const k = (th: number) => persp(phiDeg - th) / persp(restDeg);
  return {
    translateX: sampled(pos, (th) => rS * Math.sin((phiDeg - th) * RAD)),
    scaleX: sampled(pos, (th) => Math.max(0.001, Math.cos((phiDeg - th) * RAD))),
    // RN scales about the view's centre; shift so the pivot is the horizon.
    translateY: sampled(pos, (th) => (HORIZON_Y - midY) * (1 - k(th)) * s),
    scaleY: sampled(pos, k),
    opacity: sampled(pos, (th) => (Math.cos((phiDeg - th) * RAD) > 0.02 ? 1 : 0)),
    /** Facet tilt for a wall strip whose view is centred on the horizon: with
     *  perspective FACET_P, rotateY(β) makes the strip's height vary across its
     *  width at the rate g'/g, so neighbouring strips meet edge to edge. */
    // The strip already carries the curve slope of its rest angle, so tilt by
    // the DIFFERENCE in g'/g between where it is now and where it was drawn.
    facetTilt: sampled(pos, (th) => {
      const rate = (deg: number) => {
        const d = Math.max(-89, Math.min(89, wrap180(deg))) * RAD;
        return Math.sin(d) / (VIEW_D + Math.cos(d)); // g'(δ) / g(δ)
      };
      return (Math.atan((-(rate(phiDeg - th) - rate(restDeg)) * FACET_P) / R_CYL) * 180) / Math.PI;
    }),
    /** Things standing in the room aren't painted on the wall: undo the squeeze
     *  and match the vertical scale, so figures scale evenly with perspective. */
    unsqueeze: sampled(pos, (th) => k(th) / Math.max(0.3, Math.cos((phiDeg - th) * RAD))),
  };
}

type Geo = { s: number; left: number; top: number; W: number; H: number };

/** Web: browsers scroll an overflow:hidden box to show a focused child (an
 *  arrow, a statue), shoving the rotunda sideways. Pin every clip box in this
 *  tree back to 0 (scroll events don't bubble, so capture them). */
function useNoClipScroll(ref: React.RefObject<View | null>) {
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const root = ref.current as unknown as HTMLElement | null;
    if (!root?.addEventListener) return;
    const pin = (e: Event) => {
      const el = e.target as HTMLElement;
      if (el && root.contains(el) && (el.scrollLeft || el.scrollTop)) {
        el.scrollLeft = 0;
        el.scrollTop = 0;
      }
    };
    root.addEventListener('scroll', pin, true);
    return () => root.removeEventListener('scroll', pin, true);
  }, [ref]);
}

export function SelfView({
  self,
  step,
  onOpenTool,
  onDown,
  bottomInset = 0,
  userId,
}: {
  self: SelfState;
  step: NowStep | null;
  /** "Open the instrument" from a deity's focus view. */
  onOpenTool: (seatId: string) => void;
  /** Pan down to the compass / mountain peak. */
  onDown: () => void;
  bottomInset?: number;
  userId?: string;
}) {
  const t = useV10();
  const styles = useStyles();
  const insets = useSafeAreaInsets();
  const rootRef = useRef<View>(null);
  useNoClipScroll(rootRef);
  // Tapping a figure opens that deity exactly the way SyQi presents: the shared TalkingOverlay
  // (half-body figure + live voice). The guided tour still uses DeityFocus, whose ▶ arrow its script needs.
  const [focus, setFocus] = useState<{ id: string; malady: boolean } | null>(null);
  const focusId = focus?.id ?? null;
  const ob = useOnboarding();
  const tourOn = useRef(ob.active);
  tourOn.current = ob.active;
  const onOpenDeity = useCallback((id: string, malady: boolean) => {
    // The tour's focus view only knows ORDER seats; SHAPE deities always go to the talk box.
    if ((tourOn.current || malady) && SEAT_BY_ID[id]) setFocus({ id, malady });
    else openTalk(id, SEAT_BY_ID[id]?.name ?? SHAPE_BY_ID[id]?.name ?? id.toUpperCase());
  }, []);
  // Focus lifts the whole rotunda so the floor medallion sits at FOCUS_FLOOR of
  // the screen — the chosen deity stands on it, the chat fills the space below.
  const focusA = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(focusA, { toValue: focusId ? 1 : 0, duration: 380, easing: Easing.inOut(Easing.cubic), useNativeDriver: true }).start();
  }, [focusId, focusA]);
  const { width: W, height: H } = useWindowDimensions();
  // Fit the plate to the screen height; on narrow phones allow a small side
  // crop, then anchor it to the bottom (the dome carries on up into the sky).
  const s = Math.min(H / PLATE_H, W / (PLATE_W - 2 * PLATE_CROP));
  const Vw = Math.min(W, PLATE_W * s);
  const vLeft = (W - Vw) / 2;
  // Stable while the screen size holds, so the memoised rings below don't re-render on a turn.
  const geo: Geo = useMemo(() => ({ s, left: (Vw - PLATE_W * s) / 2, top: H - PLATE_H * s, W: Vw, H }), [s, Vw, H]);

  const [mode, setMode] = useState<Mode>(self.workingTier);
  const [who, setWho] = useState<Who>('current');
  // COUNCIL: a request from SyQi's voice tool / Settings takes you to the PAN floor; the X takes you back up.
  const [council, setCouncil] = useState(() => getCouncilRequest());
  const [level, setLevel] = useState<Level>(() => (getCouncilRequest() ? 'pan' : 'order'));
  const councilOn = level === 'pan';
  const shapeSeats = useShapeSeats(userId, true);
  const goLevelRef = useRef<(to: Level) => void>(() => undefined);
  useEffect(
    () =>
      onCouncil((r) => {
        setCouncil(r);
        if (r) goLevelRef.current('pan');
      }),
    [],
  );
  const cz = useCouncil({ active: councilOn, initialMembers: council?.members, topic: council?.topic });
  const [panel, setPanel] = useState<CouncilPanel>('none');
  const [seatMenu, setSeatMenu] = useState<string | null>(null);
  const [pickSlot, setPickSlot] = useState<number | null>(null);
  const leaveCouncil = () => goLevelRef.current('order');
  const councilSeats: CouncilSeats = useMemo(
    () => ({ bySlot: Array.from({ length: SLOTS }, (_x, i) => cz.members[i] ?? ''), speaking: cz.speakingId, thinking: cz.thinking, muted: cz.muted, onEmpty: setPickSlot, onSeat: setSeatMenu }),
    [cz.members, cz.speakingId, cz.thinking, cz.muted],
  );
  // The conversation sits in the empty middle of the hall: between the two lower rooms, above the floor.
  const chatW = Math.max(0.4 * W, 360 * s);
  const chatTop = geo.top + 870 * s;
  const chatBottom = bottomInset + 112;
  // The view the rotunda has come to rest on: only its five figures play their loops (none mid-turn).
  const [settled, setSettled] = useState<number | null>(() => homeStop(level, self.workingTier));
  // Tour: if the dial already sits on the tier the step is waiting for, the tap is already satisfied.
  useEffect(() => {
    if (ob.active && ob.liveTarget === `pantheon.tier.${mode}`) tourBus.emit(`pantheon:tier:${mode}`);
  }, [ob.active, ob.liveTarget, mode]);
  const trackW = Math.min(W - 32, Math.max(300, TOGGLE_W * s * 0.8));
  const segW = trackW / (LAST_IDX + 1);

  // Every stop is a turn of the rotunda: three per floor at pos 0..2 (120° each), one room set per stop.
  const dialIdx = homeStop(level, mode);
  const pos = useRef(new Animated.Value(dialIdx)).current;
  const lastSpin = useRef(dialIdx);
  const spinTo = (i: number) => {
    lastSpin.current = i;
    setSettled(null);
    Animated.timing(pos, { toValue: i, duration: TURN_MS, easing: Easing.inOut(Easing.cubic), useNativeDriver: true }).start(({ finished }) => {
      if (finished) setSettled(i);
    });
  };
  // COUNCIL opened or closed from elsewhere (SyQi, the close button): turn the ring to match.
  useEffect(() => {
    if (lastSpin.current !== dialIdx) spinTo(dialIdx);
  });
  const seg = useRef(new Animated.Value(dialIdx)).current;
  useEffect(() => {
    Animated.timing(seg, { toValue: dialIdx, duration: 360, easing: Easing.inOut(Easing.cubic), useNativeDriver: true }).start();
  }, [dialIdx, seg]);
  // The swipe may only reach unlocked stops next to the one it starts from; beyond them it rubber-bands.
  const live = useRef({ rS: R_CYL * s, idx: dialIdx, lo: 0, hi: LAST_IDX });
  live.current = { rS: R_CYL * s, idx: dialIdx, ...(([lo, hi]) => ({ lo, hi }))(reach(level, dialIdx)) };
  const turnTo = (to: number) => {
    const i = Math.max(live.current.lo, Math.min(live.current.hi, to));
    if (level === 'order') setMode(MODES[i].id);
    spinTo(i);
  };
  const turnRef = useRef(turnTo);
  turnRef.current = turnTo;
  // Changing floor: the hall is one building. The floor you are going to is built directly below (going
  // down) or above (going up) this one, and the whole tower slides a storey so it comes into place —
  // both floors are on screen the whole way, never a blank. A light blur haze rides the move.
  const fl = useRef(new Animated.Value(0)).current;
  const dirA = useRef(new Animated.Value(1)).current;
  const nextPos = useRef(new Animated.Value(0)).current;
  const [flight, setFlight] = useState<{ to: Level; dir: number; idx: number } | null>(null);
  const goLevel = (to: Level) => {
    if (to === level || flight) return;
    const dir = LEVELS.findIndex((l) => l.id === to) > LEVELS.findIndex((l) => l.id === level) ? 1 : -1;
    const from = level;
    const idx = homeStop(to, mode);
    setSettled(null);
    if (from === 'pan') {
      setPanel('none');
      setSeatMenu(null);
      setPickSlot(null);
      closeCouncil();
    }
    dirA.setValue(dir);
    nextPos.setValue(idx);
    fl.setValue(0);
    setFlight({ to, dir, idx });
    Animated.timing(fl, { toValue: 1, duration: LEVEL_MS, easing: Easing.inOut(Easing.cubic), useNativeDriver: true }).start(({ finished }) => {
      // Landed: the next floor becomes this one, at the same place on screen.
      pos.setValue(idx);
      seg.setValue(idx);
      lastSpin.current = idx;
      setLevel(to);
      setFlight(null);
      fl.setValue(0);
      if (finished) setSettled(idx);
    });
  };
  goLevelRef.current = goLevel;
  // Going down (dir 1): the tower moves up a storey; the next floor is built at +H. Up is the mirror.
  const levelY = Animated.multiply(dirA, fl.interpolate({ inputRange: [0, 1], outputRange: [0, -H] }));
  const haze = fl.interpolate({ inputRange: [0, 0.15, 0.5, 0.85, 1], outputRange: [0, 0.35, 0.45, 0.35, 0] });
  // Swipe: the back wall follows the finger (dθ = dx / R), then settles on a view.
  const start = useRef(0);
  const dragPos = (dx: number) => start.current - dx / live.current.rS / RAD / TURN_DEG;
  const swipe = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_e, g) => Math.abs(g.dx) > 12 && Math.abs(g.dx) > Math.abs(g.dy) * 1.5,
      onPanResponderGrant: () => {
        pos.stopAnimation((v) => (start.current = v));
        setSettled(null);
      },
      onPanResponderMove: (_e, g) => {
        const raw = dragPos(g.dx);
        const { lo, hi } = live.current;
        const v = raw < lo ? lo + (raw - lo) * 0.3 : raw > hi ? hi + (raw - hi) * 0.3 : raw;
        pos.setValue(v);
        seg.setValue(Math.max(lo, Math.min(hi, v)));
      },
      onPanResponderRelease: (_e, g) => turnRef.current(Math.round(dragPos(g.dx) - g.vx * 0.4)),
      onPanResponderTerminate: () => turnRef.current(live.current.idx),
    }),
  ).current;

  const rS = R_CYL * s;
  const cylX = geo.left + CYL_CX * s; // the cylinder's axis on screen
  const medX = vLeft + geo.left + MEDALLION.cx * s;
  const medY = geo.top + MEDALLION.cy * s;
  // Medallion lifted just enough that her podium clears the chat box at the bottom.
  const focusPodW = 2 * MEDALLION.r * s * 0.98;
  const focusFloorY = Math.round(H - bottomInset - FOCUS_CHAT_H - FOCUS_OPENER_H - ((PODIUM.h - PODIUM.feetY) / PODIUM.w) * focusPodW - 4);
  const lift = focusA.interpolate({ inputRange: [0, 1], outputRange: [0, focusFloorY - medY] });
  const titleCy = geo.top + TITLE_Y * s;
  const toggleCy = Math.max(titleCy + 30, geo.top + TOGGLE_Y * s);
  const dial = useMemo(() => dialArc(W / 2, toggleCy, trackW, LAST_IDX + 1), [W, toggleCy, trackW]);
  // The floor stack stands beside the dial's raised left end, in the cornice; the council's header row starts below it.
  const stackH = LEVELS.length * LEVEL_H + 8;
  const stackTop = Math.round(dial.at[0].y - stackH / 2);
  const lv = useRef(new Animated.Value(LEVELS.findIndex((l) => l.id === level))).current;
  useEffect(() => {
    Animated.timing(lv, { toValue: LEVELS.findIndex((l) => l.id === level), duration: 360, easing: Easing.inOut(Easing.cubic), useNativeDriver: true }).start();
  }, [level, lv]);
  const wv = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(wv, { toValue: WHOS.findIndex((w) => w.id === who), duration: 360, easing: Easing.inOut(Easing.cubic), useNativeDriver: true }).start();
  }, [who, wv]);
  /** One storey of the building: medallion, floor, dome, wall ring and rooms, dressed for floor `lv`, turned to `p`. */
  const hall = (lv: Level, p: Animated.Value, settledIdx: number | null, top: number) => {
    const dome = p.interpolate({ inputRange: [0, 1], outputRange: ['0deg', `${TURN_DEG}deg`] });
    const floor = p.interpolate({ inputRange: [0, 1], outputRange: ['0deg', `${-TURN_DEG}deg`] });
    return (
      <View key={lv} style={{ position: 'absolute', left: 0, top, width: Vw, height: H, overflow: 'hidden' }}>
        {/* Underfoot: the medallion turns in place; the floor and steps round it are circular, so they hold. */}
        <Animated.Image
          source={PANTHEON_MEDALLION}
          resizeMode="stretch"
          style={[disc(MEDALLION), { ...NO_TOUCH, transform: [{ rotateX: `${MEDALLION.tiltDeg}deg` }, { rotate: floor }] }]}
        />
        <Image
          source={PANTHEON_FLOOR}
          resizeMode="stretch"
          style={{ ...NO_TOUCH, position: 'absolute', left: geo.left, top: geo.top + FLOOR_TOP * s, width: PLATE_W * s, height: (PLATE_H - FLOOR_TOP) * s }}
        />
        {/* Overhead: the dome spins round the oculus. */}
        <Animated.Image
          source={PANTHEON_DOME}
          resizeMode="stretch"
          style={[disc(DOME), { ...NO_TOUCH, transform: [{ perspective: DOME.p * s }, { rotateX: `${DOME.tiltDeg}deg` }, { rotate: dome }] }]}
        />
        {/* The wall ring, as thin strips round the cylinder. */}
        <WallRing pos={p} rS={rS} s={s} cylX={cylX} top={geo.top} />
        {/* Each stop's rooms ride the same ring, dressed for this floor. */}
        {STOPS[lv].map((st, k) => (
          <RingRooms key={k} k={k} mode={lv === 'order' ? MODES[k].id : mode} self={self} current={step?.gear ?? null} geo={geo} cylX={cylX} pos={p} onOpenDeity={onOpenDeity} away={focusId} playing={settledIdx === k} who={who} locked={!!st.locked} council={lv === 'pan' && k === 0 ? councilSeats : undefined} shape={lv === 'shape' ? shapeSeats : undefined} />
        ))}
      </View>
    );
  };
  const disc = (d: { cx: number; cy: number; r: number }) => ({
    position: 'absolute' as const,
    left: geo.left + (d.cx - d.r) * s,
    top: geo.top + (d.cy - d.r) * s,
    width: 2 * d.r * s,
    height: 2 * d.r * s,
  });

  return (
    <View ref={rootRef} style={{ flex: 1 }}>
      <Animated.View style={{ position: 'absolute', left: vLeft, top: 0, width: Vw, height: H, transform: [{ translateY: Animated.add(lift, levelY) }] }} {...swipe.panHandlers}>
        {hall(level, pos, settled, 0)}
        {flight ? hall(flight.to, nextPos, null, flight.dir * H) : null}
      </Animated.View>

      {/* A light blur haze rides the move between floors. */}
      <Animated.View pointerEvents="none" style={[StyleSheet.absoluteFill, { opacity: haze, zIndex: 3 }]}>
        <BlurView intensity={40} tint="dark" style={StyleSheet.absoluteFill} />
      </Animated.View>

      {/* The floors: ORDER · SHAPE · PAN stacked down the left edge; the gold thumb rides up and down them. */}
      <View pointerEvents="box-none" style={[styles.stack, { left: 8, top: stackTop, width: LEVEL_W, height: stackH }]}>
        <Animated.View pointerEvents="none" style={[styles.thumb, { left: 4, top: 4, width: LEVEL_W - 8, height: LEVEL_H, transform: [{ translateY: Animated.multiply(lv, LEVEL_H) }] }]}>
          <LinearGradient colors={[t.goldBright, t.gold]} style={[StyleSheet.absoluteFill, { borderRadius: 999 }]} pointerEvents="none" />
        </Animated.View>
        {LEVELS.map((l) => (
          <Pressable key={l.id} onPress={() => goLevel(l.id)} style={[styles.segBtn, { height: LEVEL_H }]} hitSlop={4} accessibilityLabel={`${l.label} floor`}>
            <Text style={[styles.segText, level === l.id && { color: t.onGold }]}>{l.label}</Text>
          </Pressable>
        ))}
      </View>
      {/* Who stands on the podiums: CURRENT · DEITY · MALADY, the mirror stack right of the dial, on every floor. */}
      {(
        <View pointerEvents="box-none" style={[styles.stack, { right: 8, top: stackTop, width: WHO_W, height: stackH }]}>
          <Animated.View pointerEvents="none" style={[styles.thumb, { left: 4, top: 4, width: WHO_W - 8, height: LEVEL_H, transform: [{ translateY: Animated.multiply(wv, LEVEL_H) }] }]}>
            <LinearGradient colors={[t.goldBright, t.gold]} style={[StyleSheet.absoluteFill, { borderRadius: 999 }]} pointerEvents="none" />
          </Animated.View>
          {WHOS.map((w) => (
            <Pressable key={w.id} onPress={() => setWho(w.id)} style={[styles.segBtn, { height: LEVEL_H }]} hitSlop={4} accessibilityLabel={`Show ${w.label.toLowerCase()}`}>
              <Text style={[styles.segText, who === w.id && { color: t.onGold }]}>{w.label}</Text>
            </Pressable>
          ))}
        </View>
      )}

      {/* The dial stays still while the rotunda turns around you. */}
      <View pointerEvents="box-none" style={[StyleSheet.absoluteFill, { zIndex: 4 }]}>
        <Svg pointerEvents="none" width={W} height={dial.bottom} style={StyleSheet.absoluteFill}>
          <Path d={dial.path} stroke="rgba(214,180,106,0.6)" strokeWidth={DIAL_H + 2} strokeLinecap="round" fill="none" />
          <Path d={dial.path} stroke="rgba(9,14,32,0.92)" strokeWidth={DIAL_H} strokeLinecap="round" fill="none" />
        </Svg>
        <Animated.View
          pointerEvents="none"
          style={[styles.thumb, { left: -(segW - 6) / 2, top: -(DIAL_H - 4) / 2, width: segW - 6, height: DIAL_H - 4, transform: [{ translateX: seg.interpolate(dial.thumbX) }, { translateY: seg.interpolate(dial.thumbY) }, { rotate: seg.interpolate(dial.thumbR) }] }]}
        >
          <LinearGradient colors={[t.goldBright, t.gold]} style={[StyleSheet.absoluteFill, { borderRadius: 999 }]} pointerEvents="none" />
        </Animated.View>
        {level === 'order'
          ? MODES.map((m, i) => <TierPill key={m.id} id={m.id} label={m.label} selected={mode === m.id} width={segW} at={dial.at[i]} onPress={() => turnTo(i)} />)
          : STOPS[level].map((st, i) => (
              <Pressable key={st.label} onPress={() => turnTo(i)} disabled={st.locked} style={[styles.segBtn, dialSpot(dial.at[i], segW), st.locked && { opacity: 0.42 }]} hitSlop={4} accessibilityLabel={st.locked ? `${st.label} (locked)` : st.label}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                  {st.locked ? <Lock size={10} color="#e9e2cf" strokeWidth={2.5} /> : null}
                  <Text style={[styles.segText, dialIdx === i && { color: t.onGold }]}>{st.label}</Text>
                </View>
              </Pressable>
            ))}
      </View>

      {councilOn && !flight ? (
        <>
          <CouncilHeader c={cz} top={stackTop + stackH + 8} />
          <CouncilButtons c={cz} top={toggleCy + 30} panel={panel} setPanel={setPanel} onClose={leaveCouncil} />
          <CouncilTranscript c={cz} style={{ left: W / 2 - chatW / 2, width: chatW, top: chatTop, bottom: chatBottom }} />
          {panel === 'sessions' ? <CouncilSessions c={cz} style={{ left: 12, right: 60, top: toggleCy + 30, bottom: bottomInset + 150 }} onPicked={() => setPanel('none')} /> : null}
          <CouncilComposer c={cz} bottom={bottomInset} />
          {seatMenu ? (
            <CouncilSeatMenu
              c={cz}
              id={seatMenu}
              bottom={bottomInset + 150}
              onClose={() => setSeatMenu(null)}
              onTalk={(id) => {
                leaveCouncil();
                setTimeout(() => openTalk(id, councilSeatName(id), 'video'), 50);
              }}
              onChange={() => setPickSlot(Math.max(0, cz.members.indexOf(seatMenu)))}
            />
          ) : null}
          {pickSlot !== null ? (
            <CouncilPicker
              title="Who takes this seat?"
              options={[
                { id: 'syqi', label: 'SyQi', sub: 'the whole app' },
                ...self.all.filter((sv) => !cz.members.includes(sv.seat.id)).map((sv) => ({ id: sv.seat.id as string, label: sv.seat.name, sub: `${GEAR_LABEL[sv.seat.gear].name} · ${TIER_LABEL[sv.seat.tier]} · ${sv.seat.ability}` })),
              ].filter((o) => !cz.members.includes(o.id))}
              bottom={bottomInset + 150}
              onPick={(id) => cz.seat(id, pickSlot)}
              onClose={() => setPickSlot(null)}
            />
          ) : null}
        </>
      ) : null}

      <EdgeTabs bottom={bottomInset} down={onDown} downTargetId="pantheon.down" labels={{ down: 'Down to the mountain' }} />
      {/* "?" explainer: centred above the CURRENT / DEITY / MALADY stack, kept below the header buttons. */}
      {focusId ? null : (
        <ScreenHelp help={pantheonHelp} style={{ position: 'absolute', zIndex: 6, right: 8 + (WHO_W - 26) / 2, top: Math.max(insets.top + 56, stackTop - 34) }} />
      )}
      {focusId ? (
        <DeityFocus
          seatId={focusId}
          asMalady={!!focus?.malady}
          floorX={medX}
          floorY={focusFloorY}
          podiumW={focusPodW}
          chatH={FOCUS_CHAT_H}
          seatView={self.all.find((v) => v.seat.id === focusId)}
          step={step}
          onClose={() => setFocus(null)}
          // The focus stays open: the tool's ◀ comes straight back to this conversation.
          onOpenTool={onOpenTool}
        />
      ) : null}
    </View>
  );
}

/** The wall ring as thin strips round the cylinder. Memoised: it never changes on a turn (pos drives it natively). */
const WallRing = React.memo(function WallRing({ pos, rS, s, cylX, top }: { pos: Animated.Value; rS: number; s: number; cylX: number; top: number }) {
  const strips = useMemo(
    () =>
      MODES.flatMap((_m, k) =>
        Array.from({ length: STRIPS }, (_x, j) => {
          const rest = -TURN_DEG / 2 + (j + 0.5) * (TURN_DEG / STRIPS);
          return { key: `${k}-${j}`, j, cyl: onCylinder(pos, k * TURN_DEG + rest, rS, rest, WALL_BOTTOM / 2, s) };
        }),
      ),
    [pos, rS, s],
  );
  return (
    <>
        {strips.map(({ key, j, cyl }) => (
          // Centred on the horizon so scaleY and the facet tilt both pivot there.
          <Animated.View
            key={key}
            pointerEvents="none"
            style={{
              position: 'absolute',
              left: cylX - (STRIP_W * s) / 2,
              top: top + (HORIZON_Y - STRIP_HALF) * s,
              width: STRIP_W * s + 1,
              height: 2 * STRIP_HALF * s,
              overflow: 'hidden',
              opacity: cyl.opacity,
              transform: [
                { translateX: cyl.translateX },
                { scaleX: cyl.scaleX },
                { scaleY: cyl.scaleY },
                { perspective: FACET_P * s },
                { rotateY: cyl.facetTilt.interpolate({ inputRange: [-90, 90], outputRange: ['-90deg', '90deg'] }) },
              ],
            }}
          >
            <Image source={PANTHEON_WALLS} resizeMode="stretch" style={{ position: 'absolute', left: -j * STRIP_W * s, top: (STRIP_HALF - HORIZON_Y) * s, width: WALL_TEX_W * s, height: WALL_BOTTOM * s }} />
          </Animated.View>
        ))}
    </>
  );
});

/** View k's five rooms on the ring: each a clip window at its wall angle. Memoised so a turn
 *  (which only changes `mode` up top) doesn't re-render all fifteen rooms. */
const RingRooms = React.memo(function RingRooms({ k, mode, self, current, geo, cylX, pos, onOpenDeity, away, playing, who, locked, council, shape }: { k: number; mode: Mode; self: SelfState; current: GearId | null; geo: Geo; cylX: number; pos: Animated.Value; onOpenDeity: (id: string, malady: boolean) => void; away: string | null; playing: boolean; who: Who; /** A locked stop: bare rooms, nobody on the podiums. */ locked: boolean; council?: CouncilSeats; shape?: ShapeSeats }) {
  const styles = useStyles();
  const { s, top } = geo;
  const rS = R_CYL * s;
  const rooms = useMemo(
    () =>
      ROOMS.map((room) => {
        const u0 = wallU(room.x0);
        const u1 = wallU(room.x1);
        const rest = (((u0 + u1) / 2 - WALL_TEX_W / 2) / R_CYL) / RAD;
        const midY = room.apex + (room.bottom - room.apex + ROOM_FOOT) / 2;
        return { room, u0, u1, cyl: onCylinder(pos, k * TURN_DEG + rest, rS, rest, midY, s), labelCyl: onCylinder(pos, k * TURN_DEG + rest, rS, rest, room.label.y, s) };
      }),
    [k, pos, rS, s],
  );
  return (
    <>
      {rooms.map(({ room, u0, u1, cyl, labelCyl }) => {
        const w = (u1 - u0) * s;
        const name = (shape ? SECTOR_LABELS[ROOM_SECTOR[room.gear]] : GEAR_LABEL[room.gear].name).toUpperCase();
        const lh = 34 * s;
        return (
          <React.Fragment key={room.gear}>
          {/* The gear's name carved into the wall band over the arch: rides the wall like the paint does
              (squeezes with it, no unsqueeze) and tilts with the band's perspective slope. */}
          <Animated.View
            pointerEvents="none"
            style={{
              position: 'absolute', left: cylX - w / 2, top: top + room.label.y * s - lh / 2, width: w, height: lh,
              alignItems: 'center', justifyContent: 'center', opacity: labelCyl.opacity,
              transform: [{ translateX: labelCyl.translateX }, { translateY: labelCyl.translateY }, { scaleX: labelCyl.scaleX }, { scaleY: labelCyl.scaleY }, { rotate: `${room.label.deg}deg` }],
            }}
          >
            <Text style={[styles.carved, { fontSize: Math.min(Math.max(9, 21 * s), (w * 0.86) / (name.length * 0.78)) }]} numberOfLines={1}>{name}</Text>
          </Animated.View>
          <Animated.View
            pointerEvents="box-none"
            style={{
              position: 'absolute',
              left: cylX - w / 2,
              top: top + room.apex * s,
              width: w,
              height: (room.bottom - room.apex + ROOM_FOOT) * s,
              overflow: 'hidden',
              opacity: cyl.opacity,
              transform: [{ translateX: cyl.translateX }, { translateY: cyl.translateY }, { scaleX: cyl.scaleX }, { scaleY: cyl.scaleY }],
            }}
          >
            {locked ? null : <RoomStage room={room} u0={u0} width={w} s={s} mode={mode} self={self} lit={room.gear === current} unsqueeze={cyl.unsqueeze} onOpenDeity={onOpenDeity} away={away} playing={playing} who={who} council={council} shape={shape} />}
          </Animated.View>
          </React.Fragment>
        );
      })}
    </>
  );
});

/** One room: its tablet with ◀ ▶ on the wall, and its three statues on a small
 *  turntable — the one in front centred and lit, the other two a step back, dimmed. */
function RoomStage({ room, u0, width, s, mode, self, lit, unsqueeze, onOpenDeity, away, playing, who, council, shape }: { room: Room; u0: number; width: number; s: number; mode: Mode; self: SelfState; lit: boolean; unsqueeze: Animated.AnimatedInterpolation<number>; onOpenDeity: (id: string, malady: boolean) => void; away: string | null; playing: boolean; who: Who; council?: CouncilSeats; shape?: ShapeSeats }) {
  const styles = useStyles();
  // This room's one deity for this tier, on the front podium.
  const v = self.all.find((sv) => sv.seat.gear === room.gear && sv.seat.tier === mode);
  // CURRENT: whoever holds the seat (the SPECTRUM decides); DEITY / MALADY: always that one.
  const maladyOf = (sv: SeatView | undefined) => who === 'malady' || (who === 'current' && sv?.holder === 'malady');
  const asMalady = maladyOf(v);
  const still = useRef(new Animated.Value(0)).current;
  const ox = (wallU(room.main.cx) - u0) * s;
  const feetY = (room.main.feet - room.apex) * s;
  const plH = Math.max(22, 30 * s);
  if (shape) {
    // SHAPE: this room's sector deity, from the Goal picked in ATLAS; none (or no figure yet) → empty podium.
    // A tap talks to that deity; an empty podium sends you to ATLAS, where the Goal is chosen.
    const sd = shape[ROOM_SECTOR[room.gear]];
    // SHAPE deities have no maladies in the app (no names, no art): MALADY leaves the podium bare.
    if (who === 'malady') return null;
    return sd ? (
      <Statue i={0} v={{ seat: { id: sd.id, name: sd.name } }} rot={still} lit={false} ox={ox} feetY={feetY} rr={0} backScale={1} backLift={0} h={room.main.h * s} pw={room.main.pw * s} unsqueeze={unsqueeze} playing={playing} onPress={() => onOpenDeity(sd.id, false)} />
    ) : (
      <EmptyStand ox={ox} feetY={feetY} h={room.main.h * s} pw={room.main.pw * s} unsqueeze={unsqueeze} onPress={() => onOpenDeity('telos', false)} />
    );
  }
  if (council) {
    // COUNCIL: this room's podium is one of five council seats — whoever you put here, or empty (+).
    const slot = ROOMS.indexOf(room);
    const id = council.bySlot[slot];
    const cv = id ? self.all.find((sv) => sv.seat.id === id) : undefined;
    if (id === 'syqi') return <EmptyStand ox={ox} feetY={feetY} h={room.main.h * s} pw={room.main.pw * s} unsqueeze={unsqueeze} syqi lit={council.speaking === 'syqi'} dim={council.muted.includes('syqi')} tag={council.thinking === 'syqi' ? '…' : council.muted.includes('syqi') ? 'MUTED' : undefined} onPress={() => council.onSeat('syqi')} />;
    return cv ? (
      <Statue i={0} v={cv} malady={maladyOf(cv)} rot={still} lit={council.speaking === cv.seat.id} ox={ox} feetY={feetY} rr={0} backScale={1} backLift={0} h={room.main.h * s} pw={room.main.pw * s} unsqueeze={unsqueeze} playing={playing && !council.muted.includes(cv.seat.id)} dim={council.muted.includes(cv.seat.id)} tag={council.thinking === cv.seat.id ? '…' : council.muted.includes(cv.seat.id) ? 'MUTED' : undefined} onPress={() => council.onSeat(cv.seat.id)} />
    ) : (
      <EmptyStand ox={ox} feetY={feetY} h={room.main.h * s} pw={room.main.pw * s} unsqueeze={unsqueeze} onPress={() => council.onEmpty(slot)} />
    );
  }
  return (
    <>
      {/* The deity you summoned has left her podium for the centre of the hall. */}
      {v && v.seat.id !== away ? (
        <Statue i={0} v={v} malady={asMalady} rot={still} lit={lit} ox={ox} feetY={feetY} rr={0} backScale={1} backLift={0} h={room.main.h * s} pw={room.main.pw * s} unsqueeze={unsqueeze} playing={playing} onPress={() => {
          if (!asMalady) tourBus.emit(`deity:open:${v.seat.id}`);
          onOpenDeity(v.seat.id, asMalady);
        }} />
      ) : null}
    </>
  );
}

/** A figure on its podium. Its box is centred on the feet, so any scaling keeps
 *  it standing on the same spot. (rot/rr/backScale/backLift drive the old room
 *  turntable; rooms now hold one deity, so they are passed as identity.) */
/** Where one dial stop sits on the arc: its centre on screen and the tangent's tilt (radians). */
interface DialSpot { x: number; y: number; rot: number }
/**
 * The dial as a circular arc sagging below its ends (the roof's coffer band seen from beneath):
 * `n` equal stops along `trackW` of arc, the middle of the arc at (cx, midY). Returns the stroke path
 * for the track, each stop's spot, and interpolations that keep the gold thumb riding the curve.
 */
function dialArc(cx: number, midY: number, trackW: number, n: number) {
  const sag = DIAL_SAG * trackW;
  const R = (trackW * trackW) / 4 / (2 * sag) + sag / 2;
  const step = trackW / n / R; // radians per stop
  const pt = (th: number) => ({ x: cx + R * Math.sin(th), y: midY - R + R * Math.cos(th), rot: -th });
  const thAt = (k: number) => (k - (n - 1) / 2) * step;
  const half = ((n - 1) / 2) * step;
  const reach = (n / 2) * step - DIAL_H / 2 / R; // track ends flush with the end stops (round caps add the rest)
  const path = Array.from({ length: 33 }, (_x, i) => pt(-reach + (2 * reach * i) / 32))
    .map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(' ');
  const inputRange: number[] = [];
  for (let k = 0; k <= (n - 1) * 8; k++) inputRange.push(k / 8);
  const along = inputRange.map((k) => pt(thAt(k)));
  return {
    path,
    at: Array.from({ length: n }, (_x, k) => pt(thAt(k))),
    bottom: Math.ceil(midY + DIAL_H),
    thumbX: { inputRange, outputRange: along.map((p) => p.x), extrapolate: 'clamp' as const },
    thumbY: { inputRange, outputRange: along.map((p) => p.y), extrapolate: 'clamp' as const },
    thumbR: { inputRange, outputRange: along.map((p) => `${p.rot}rad`), extrapolate: 'clamp' as const },
  };
}
/** A dial stop's box: centred on its spot and tilted with the arc. */
const dialSpot = (at: DialSpot, width: number) => ({ position: 'absolute' as const, left: at.x - width / 2, top: at.y - DIAL_H / 2, width, height: DIAL_H, transform: [{ rotate: `${at.rot}rad` }] });

/** One DEMI / TYTN / OLMPN segment button; a tour target that reports the tap on the bus. */
function TierPill({ id, label, selected, width, at, onPress }: { id: TierId; label: string; selected: boolean; width: number; at: DialSpot; onPress: () => void }) {
  const styles = useStyles();
  const t = useV10();
  const tour = useTourTarget(`pantheon.tier.${id}`);
  return (
    <Pressable
      ref={tour.onLayoutRef}
      collapsable={false}
      onPress={() => {
        tourBus.emit(`pantheon:tier:${id}`);
        onPress();
      }}
      style={[styles.segBtn, dialSpot(at, width)]}
      hitSlop={4}
    >
      <Text style={[styles.segText, selected && { color: t.onGold }]}>{label}</Text>
    </Pressable>
  );
}

/** Who stands on a podium: an ORDER seat (SeatView) or a SHAPE deity (just id + name). */
type StatueWho = { seat: { id: string; name: string } } & Partial<Pick<SeatView, 'maladyId' | 'maladyName'>>;

function Statue({ i, v, malady = false, rot, lit, ox, feetY, rr, backScale, backLift, h, pw, unsqueeze, playing, onPress, dim = false, tag }: {
  i: number; v: StatueWho; /** Show the seat's malady instead of its deity. */ malady?: boolean; rot: Animated.Value; lit: boolean; ox: number; feetY: number; rr: number; backScale: number; backLift: number; h: number; pw: number;
  unsqueeze: Animated.AnimatedInterpolation<number>; playing: boolean; onPress: () => void; /** Council: muted member. */ dim?: boolean; /** Council: a word over the figure ('…' thinking, 'MUTED'). */ tag?: string;
}) {
  const styles = useStyles();
  const tour = useTourTarget(`statue.${v.seat.id}`);
  const showDeity = !malady;
  const art = showDeity ? fullBodyFor(v.seat.id) ?? shapeFullFor(v.seat.id) : maladyFullFor(v.maladyId ?? '', v.seat.id);
  const src = art?.src;
  const clips = showDeity ? HALL_CLIPS[v.seat.id] : undefined; // living idle loop, when the deity has one
  const fw = h * (clips?.aspect ?? art?.aspect ?? 0.45);
  const name = showDeity ? v.seat.name : v.maladyName || 'Malady';
  const anim = useMemo(() => {
    const IN: number[] = [];
    for (let r = -6; r <= 9; r += 0.125) IN.push(r);
    const ang = (r: number) => ((i - r) * 120 * Math.PI) / 180;
    const depth = (r: number) => (Math.cos(ang(r)) + 0.5) / 1.5; // 1 front … 0 back
    const f = (fn: (r: number) => number) => rot.interpolate({ inputRange: IN, outputRange: IN.map(fn), extrapolate: 'clamp' });
    return {
      x: f((r) => rr * Math.sin(ang(r))),
      y: f((r) => -(1 - depth(r)) * backLift),
      sc: f((r) => backScale + (1 - backScale) * depth(r)),
      op: f((r) => 0.14 + 0.86 * depth(r) ** 3), // back two: dark, barely there (Joel, 2026-10-10)
    };
  }, [i, rot, rr, backLift, backScale]);
  const boxW = Math.max(fw, pw) + 8;
  const ped = podiumDims(pw);
  return (
    <Animated.View
      pointerEvents="box-none"
      style={{
        position: 'absolute',
        left: ox - boxW / 2,
        top: feetY - h,
        width: boxW,
        height: 2 * h,
        opacity: dim ? Animated.multiply(anim.op, 0.45) : anim.op,
        transform: [{ translateX: anim.x }, { translateY: anim.y }, { scaleX: Animated.multiply(anim.sc, unsqueeze) }, { scaleY: anim.sc }],
      }}
    >
      {tag ? (
        <View pointerEvents="none" style={[styles.seatTag, { left: boxW / 2 - 28, top: ped.feet * 0.25 - 10 }]}>
          <Text style={styles.seatTagText}>{tag}</Text>
        </View>
      ) : null}
      {lit ? (
        // A faint, soft aura that fades out well inside its own box (no hard edge).
        <Svg pointerEvents="none" width={fw * 1.8} height={h * 1.15} style={{ position: 'absolute', left: boxW / 2 - fw * 0.9, top: -h * 0.05 }}>
          <Defs>
            <RadialGradient id={`g-${v.seat.id}`} cx="50%" cy="50%" r="50%">
              <Stop offset="0" stopColor="#f0cf85" stopOpacity={0.22} />
              <Stop offset="0.5" stopColor="#e2c27a" stopOpacity={0.08} />
              <Stop offset="1" stopColor="#d6b46a" stopOpacity={0} />
            </RadialGradient>
          </Defs>
          <Ellipse cx={fw * 0.9} cy={h * 0.575} rx={fw * 0.88} ry={h * 0.56} fill={`url(#g-${v.seat.id})`} />
        </Svg>
      ) : null}
      {/* Tour target: the union of the podium and figure hit areas (never touchable itself). */}
      <View
        ref={tour.onLayoutRef}
        collapsable={false}
        pointerEvents="none"
        style={{
          position: 'absolute',
          left: (boxW - Math.max(fw, pw)) / 2,
          width: Math.max(fw, pw),
          top: Math.min(ped.feet * 0.25, h - ped.feet),
          height: Math.max(ped.feet * 0.25 + h, h - ped.feet + ped.h) - Math.min(ped.feet * 0.25, h - ped.feet),
        }}
      />
      <Pressable onPress={onPress} style={{ position: 'absolute', left: (boxW - pw) / 2, top: h - ped.feet, width: pw, height: ped.h, outlineWidth: 0 } as object}>
        <Image source={PANTHEON_PODIUM} style={{ width: pw, height: ped.h }} resizeMode="stretch" />
        <View pointerEvents="none" style={[styles.podiumFace, { top: ped.band - ped.h * 0.16, height: ped.h * 0.32, left: (pw - ped.bandW) / 2, width: ped.bandW }]}>
          {/* Sized to fit the band (web has no adjustsFontSizeToFit). */}
          <Text style={[styles.engraved, { fontSize: Math.max(6, Math.min(ped.h * 0.24, (ped.bandW * 0.8) / (name.length * 0.8 + 0.6))), letterSpacing: 1 }]} numberOfLines={1}>
            {name.toUpperCase()}
          </Text>
        </View>
      </Pressable>
      {src ? (
        <Pressable onPress={onPress} style={{ position: 'absolute', left: (boxW - fw) / 2, top: ped.feet * 0.25, width: fw, height: h, outlineWidth: 0 } as object}>
          {clips ? <HallFigure set={clips} width={fw} height={h} active={playing} /> : <Image source={src} style={{ width: fw, height: h }} resizeMode="contain" />}
        </Pressable>
      ) : null}
    </Animated.View>
  );
}

/** COUNCIL: an empty podium with a + above it — tap to pick who sits here. */
function EmptyStand({ ox, feetY, h, pw, unsqueeze, onPress, syqi = false, lit = false, dim = false, tag }: { ox: number; feetY: number; h: number; pw: number; unsqueeze: Animated.AnimatedInterpolation<number>; onPress: () => void; /** SyQi on this podium (her full-body still; she has no hall loop yet). */ syqi?: boolean; lit?: boolean; dim?: boolean; tag?: string }) {
  const styles = useStyles();
  const ped = podiumDims(pw);
  const boxW = pw + 8;
  const plus = Math.max(30, pw * 0.3);
  return (
    <Animated.View pointerEvents="box-none" style={{ position: 'absolute', left: ox - boxW / 2, top: feetY - h, width: boxW, height: 2 * h, opacity: dim ? 0.45 : 1, transform: [{ scaleX: unsqueeze }] }}>
      {tag ? (
        <View pointerEvents="none" style={[styles.seatTag, { left: boxW / 2 - 28, top: ped.feet * 0.25 - 10 }]}>
          <Text style={styles.seatTagText}>{tag}</Text>
        </View>
      ) : null}
      <Pressable onPress={onPress} accessibilityLabel={syqi ? 'SyQi, seated' : 'Seat a deity here'} style={{ position: 'absolute', left: (boxW - pw) / 2, top: 0, width: pw, height: h + ped.h, alignItems: 'center', outlineWidth: 0 } as object}>
        {syqi && lit ? (
          // Same soft aura the deities get while they speak.
          <Svg pointerEvents="none" width={pw * 1.2} height={h * 1.15} style={{ position: 'absolute', left: -pw * 0.1, top: -h * 0.05 }}>
            <Defs>
              <RadialGradient id="g-syqi" cx="50%" cy="50%" r="50%">
                <Stop offset="0" stopColor="#f0cf85" stopOpacity={0.22} />
                <Stop offset="0.5" stopColor="#e2c27a" stopOpacity={0.08} />
                <Stop offset="1" stopColor="#d6b46a" stopOpacity={0} />
              </RadialGradient>
            </Defs>
            <Ellipse cx={pw * 0.6} cy={h * 0.575} rx={pw * 0.58} ry={h * 0.56} fill="url(#g-syqi)" />
          </Svg>
        ) : null}
        {syqi ? (
          <Image source={fullBodyFor('syqi')!.src} resizeMode="contain" style={{ position: 'absolute', top: ped.feet * 0.25, width: h * fullBodyFor('syqi')!.aspect, height: h }} />
        ) : (
        <View style={[styles.plusBox, { width: plus, height: plus, borderRadius: plus / 2, marginTop: h * 0.45 }]}>
          <Text style={[styles.plusText, { fontSize: plus * 0.6, lineHeight: plus * 0.7 }]}>+</Text>
        </View>
        )}
        <Image source={PANTHEON_PODIUM} style={{ position: 'absolute', top: h - ped.feet, width: pw, height: ped.h, opacity: 0.85 }} resizeMode="stretch" />
        <View pointerEvents="none" style={[styles.podiumFace, { top: h - ped.feet + ped.band - ped.h * 0.16, height: ped.h * 0.32, left: (pw - ped.bandW) / 2, width: ped.bandW }]}>
          <Text style={[styles.engraved, { fontSize: Math.max(6, ped.h * 0.2), letterSpacing: 1 }]} numberOfLines={1}>
            {syqi ? 'SYQI' : 'SEAT'}
          </Text>
        </View>
      </Pressable>
    </Animated.View>
  );
}

/** The mockup pedestal scaled to width w: total height, feet line, name band. */
function podiumDims(w: number) {
  const k = w / PODIUM.w;
  return { h: PODIUM.h * k, feet: PODIUM.feetY * k, band: PODIUM.bandY * k, bandW: PODIUM.bandW * k };
}

/** The mockup's banner marks: Revise ☾, Orient ○, Regulate ⚭, Execute △, Direct ◯. */
function GearSymbol({ gear, size, color }: { gear: GearId; size: number; color: string }) {
  const sw = 2.2;
  const c = size / 2;
  const r = size / 2 - sw;
  return (
    <Svg width={size} height={size}>
      {gear === 'revise' ? (
        <Path d={`M ${c + r * 0.25} ${c - r} A ${r} ${r} 0 1 0 ${c + r * 0.25} ${c + r} A ${r * 0.78} ${r * 0.78} 0 1 1 ${c + r * 0.25} ${c - r} Z`} fill="none" stroke={color} strokeWidth={sw} />
      ) : gear === 'orient' ? (
        <Circle cx={c} cy={c} r={r * 0.62} fill="none" stroke={color} strokeWidth={sw} />
      ) : gear === 'regulate' ? (
        <>
          <Circle cx={c - r * 0.32} cy={c} r={r * 0.6} fill="none" stroke={color} strokeWidth={sw} />
          <Circle cx={c + r * 0.32} cy={c} r={r * 0.6} fill="none" stroke={color} strokeWidth={sw} />
        </>
      ) : gear === 'execute' ? (
        <Polygon points={`${c},${c - r} ${c + r},${c + r * 0.8} ${c - r},${c + r * 0.8}`} fill="none" stroke={color} strokeWidth={sw} strokeLinejoin="round" />
      ) : (
        <Circle cx={c} cy={c} r={r} fill="none" stroke={color} strokeWidth={sw} />
      )}
    </Svg>
  );
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    thumb: { position: 'absolute', borderRadius: 999, overflow: 'hidden', boxShadow: `0 0 12px ${t.goldGlow}` } as object,
    segBtn: { alignItems: 'center', justifyContent: 'center', outlineWidth: 0 } as object,
    segText: { color: '#e9e2cf', fontSize: 11.5, fontWeight: '800', letterSpacing: 0.4 },
    // The floor stack: the dial's dark track stood on end, with the same gold rim.
    stack: { position: 'absolute', zIndex: 5, paddingVertical: 4, borderRadius: 999, backgroundColor: 'rgba(9,14,32,0.92)', borderWidth: 1, borderColor: 'rgba(214,180,106,0.6)' },
    tabletRow: { position: 'absolute', left: 0, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 3, zIndex: 5 },
    // Gilded inscription on the wall band: light gold letters with a dark cut shadow, legible on the bright gold.
    carved: { fontFamily: DISPLAY_FONT, color: '#fff1cc', letterSpacing: 2.5, textShadowColor: 'rgba(22,13,2,0.95)', textShadowRadius: 3, textShadowOffset: { width: 0, height: 1.5 } },
    roomName: { fontFamily: DISPLAY_FONT, color: '#ffffff', letterSpacing: 1.2, paddingHorizontal: 2, textShadowColor: 'rgba(0,0,0,0.85)', textShadowRadius: 6, textShadowOffset: { width: 0, height: 1 } },
    // Podium name: light gold with a dark cut shadow (the dark-on-gold engraving was barely legible).
    engraved: { fontFamily: DISPLAY_FONT, color: '#fff1cc', letterSpacing: 1.6, textShadowColor: 'rgba(22,13,2,0.95)', textShadowOffset: { width: 0, height: 1 }, textShadowRadius: 2.5 },
    podiumFace: { position: 'absolute', alignItems: 'center', justifyContent: 'center' },
    plusBox: { alignItems: 'center', justifyContent: 'center', borderWidth: 1.5, borderColor: 'rgba(168,196,240,0.45)', backgroundColor: 'rgba(120,150,210,0.14)' },
    plusText: { color: 'rgba(190,210,245,0.85)', fontWeight: '300' },
    seatTag: { position: 'absolute', width: 56, alignItems: 'center', paddingVertical: 2, borderRadius: 999, backgroundColor: 'rgba(9,14,32,0.9)', borderWidth: 1, borderColor: 'rgba(214,180,106,0.7)', zIndex: 3 },
    seatTagText: { color: '#f0cf85', fontSize: 9, fontWeight: '900', letterSpacing: 1 },
    foot: { position: 'absolute', left: 16, right: 16, textAlign: 'center', color: '#f3ead2', fontSize: 11, fontWeight: '600', textShadowColor: 'rgba(0,0,0,0.9)', textShadowRadius: 8 },
  }),
);
